import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { format, parseISO, isValid } from "date-fns";
import styles from "./payment-notification.module.css";
import { fetchPaymentNotificationNotes, createPaymentNotificationNote } from "./payment-notification.api";
import Loader from "../../components/loader/loader";

function formatNoteTimestamp(raw) {
  if (!raw) return "—";
  try {
    const d = parseISO(String(raw));
    if (isValid(d)) return format(d, "dd/MM/yyyy HH:mm");
  } catch {
    /* fall through */
  }
  return String(raw);
}

function PaymentNotificationNotes({
  notificationId,
  token,
  username,
  onNotesChange,
}) {
  const [notes, setNotes] = useState([]);
  const [draftNote, setDraftNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [postError, setPostError] = useState(null);
  const [posting, setPosting] = useState(false);
  const onNotesChangeRef = useRef(onNotesChange);
  const notesListRef = useRef(null);

  useEffect(() => {
    onNotesChangeRef.current = onNotesChange;
  }, [onNotesChange]);

  useEffect(() => {
    if (notificationId == null || !token) {
      setNotes([]);
      setDraftNote("");
      setLoadError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadNotes = async () => {
      setLoading(true);
      setLoadError(null);
      setPostError(null);
      setDraftNote("");
      setPosting(false);

      try {
        const results = await fetchPaymentNotificationNotes(notificationId, token);
        if (cancelled) return;
        setNotes(results);
        onNotesChangeRef.current?.(results.length);
      } catch (err) {
        if (cancelled) return;
        console.error("Error fetching payment notification notes", err);
        setNotes([]);
        setLoadError(
          err.response?.data?.detail ||
            err.message ||
            "Could not load notes."
        );
        onNotesChangeRef.current?.(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadNotes();

    return () => {
      cancelled = true;
    };
  }, [notificationId, token]);

  const sortedNotes = useMemo(
    () =>
      [...notes].sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      ),
    [notes]
  );

  useEffect(() => {
    const list = notesListRef.current;
    if (!list || loading) return;
    requestAnimationFrame(() => {
      list.scrollTop = list.scrollHeight;
    });
  }, [sortedNotes, loading]);

  const submitNote = async () => {
    const trimmed = draftNote.trim();
    if (!trimmed || posting || notificationId == null || !token) return;

    setPosting(true);
    setPostError(null);

    try {
      const created = await createPaymentNotificationNote(
        notificationId,
        trimmed,
        token
      );
      setNotes((prev) => {
        const next = [...prev, created];
        onNotesChange?.(next.length);
        return next;
      });
      setDraftNote("");
    } catch (err) {
      console.error("Error posting payment notification note", err);
      setPostError(
        err.response?.data?.detail ||
          err.message ||
          "Could not post note."
      );
    } finally {
      setPosting(false);
    }
  };

  const handlePostNote = (event) => {
    event.preventDefault();
    submitNote();
  };

  const handleNoteKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitNote();
    }
  };

  const getUserDisplayName = (userObject) => {
    if (!userObject) return "Unknown";
    return `${userObject.first_name} ${userObject.last_name}`;
  }

  return (
    <section className={styles.notesSection} aria-labelledby="notification-notes-heading">
      <h3 id="notification-notes-heading" className={styles.notesSectionTitle}>
        Notes
      </h3>

      <div className={styles.notesList} ref={notesListRef}>
        {loading ? (
          <div className={styles.notesLoading}>
            <Loader />
            <span>Loading notes…</span>
          </div>
        ) : loadError ? (
          <p className={styles.notesError}>{loadError}</p>
        ) : sortedNotes.length === 0 ? (
          <p className={styles.notesEmpty}>No notes yet.</p>
        ) : (
          sortedNotes.map((entry) => (
            <article key={entry.id} className={styles.noteItem}>
              <header className={styles.noteItemHeader}>
                <span className={styles.noteAuthor}>{getUserDisplayName(entry.created_by)}</span>
                <time
                  className={styles.noteTimestamp}
                  dateTime={entry.created_at}
                >
                  {formatNoteTimestamp(entry.created_at)}
                </time>
              </header>
              <p className={styles.noteMessage}>{entry.note}</p>
            </article>
          ))
        )}
      </div>

      <Form className={styles.noteComposer} onSubmit={handlePostNote}>
        <Form.Group controlId="notification-note-input">
          <Form.Label className={styles.noteComposerLabel}>
            Add a note
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Enter your note here…"
            value={draftNote}
            onChange={(event) => setDraftNote(event.target.value)}
            onKeyDown={handleNoteKeyDown}
            disabled={posting || loading}
          />
        </Form.Group>
        {postError ? <p className={styles.notesPostError}>{postError}</p> : null}
        <div className={styles.noteComposerActions}>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={posting || loading || !draftNote.trim()}
          >
            {posting ? "Posting…" : "Post note"}
          </Button>
        </div>
      </Form>
    </section>
  );
}

PaymentNotificationNotes.propTypes = {
  notificationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  token: PropTypes.string,
  username: PropTypes.string,
  onNotesChange: PropTypes.func,
};

export default PaymentNotificationNotes;
